/**
 * Supabase persistence for the store.
 *
 * The whole tenant is loaded in one go. That is a deliberate choice for this product:
 * a landlord with 25 units and a few thousand bookings is a payload of well under a
 * megabyte, and holding it in memory keeps every view synchronous and instant on a
 * phone with patchy reception. If a customer ever outgrows it, the seam to add windowed
 * loading is `loadDatabase` alone.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type {
  Booking, Database, Guest, Invoice, InvoiceItem, Payment, Quote,
} from './model'
import type { TouristTaxRule } from '../lib/pricing'
import type { Stufe } from '../lib/rollen'
import type { AvvFeld, AvvKanal, AvvStatus } from '../lib/avv'
import { kaufwegSichtbar } from '../lib/kaufweg'

export interface TenantBundle {
  db: Database
  touristTax: TouristTaxRule | null
  subscription: Subscription | null
}

/**
 * Die Abrechnungsseite des Mandanten, soweit der Store sie braucht.
 *
 * `units_limit` bedeutet seit 0060 nicht mehr „bei der Anlage kopierter Wert",
 * sondern „von Hand gesetzte Ausnahme"; `null` heißt: es gilt das Band des
 * Pakets. `plan_pending_code` trägt eine Herabstufung, die zum Periodenende
 * wirkt — sie wird beim Lesen ausgewertet und nicht von einem Cron
 * materialisiert.
 */
export interface Subscription {
  plan_code: string
  status: string
  contract_term: 'monthly' | 'yearly'
  trial_ends_at: string | null
  current_period_end: string | null
  units_limit: number | null
  plan_pending_code: string | null
  plan_pending_at: string | null
}

function must<T>(res: { data: T | null; error: { message: string } | null }, what: string): T {
  if (res.error) throw new Error(`${what}: ${res.error.message}`)
  return (res.data ?? []) as T
}

/**
 * Der Betrieb des angemeldeten Nutzers samt seiner Stufe — oder null, wenn er
 * noch keinen hat.
 *
 * WARUM HIER EIN `order` STEHT: Vorher stand hier `.limit(1)` ohne Sortierung.
 * Bei genau einer Mitgliedschaft ist das gleichgültig, und mehr gab es bisher
 * nie. Sobald jemand in zwei Betrieben steht, entscheidet ohne `order` die
 * Laune des Planers, welcher geladen wird — und seit Vorgang 013 hängt daran
 * nicht mehr nur der Mandant, sondern auch die angenommene STUFE. Ein Münzwurf
 * darüber, ob die Oberfläche Knöpfe zeigt, ist kein Zustand, den man stehen
 * lässt, bis er auffällt.
 *
 * Sortiert wird nach Stufe, stärkste zuerst: Wer irgendwo Inhaber ist, landet
 * in seinem eigenen Betrieb und nicht in dem, in dem er nur zuschaut.
 *
 * WARUM DER `tenants!inner`-JOIN AUF `active` STEHT (Vorgang 019): Ein
 * deaktivierter Mandant — Betrieb aufgelöst, weil sein einziger Owner das Konto
 * gelöscht hat — ist vollständig zu. `current_tenant_ids()` filtert ihn seit
 * Migration v019 serverseitig aus jeder Policy; ohne denselben Filter hier
 * bliebe die Mitgliedschaftszeile aber wählbar, und ein verbliebenes Mitglied
 * landete in einer leeren Hülle statt (ohne weiteren Mandanten) sauber im
 * Onboarding-Zustand von `auth.tsx`. Der `eq`-Filter ist wirkungsgleich mit dem
 * Server-Helper; der Inner-Join allein trüge schon, weil die RLS der `tenants`
 * die deaktivierte Zeile gar nicht mehr zeigt.
 */
export async function findMyTenant(
  sb: SupabaseClient,
): Promise<{ tenantId: string; rolle: Stufe } | null> {
  const { data, error } = await sb
    .from('memberships')
    .select('tenant_id,role,tenants!inner(active)')
    .eq('tenants.active', true)
    .order('role', { ascending: true })   // owner < manager < staff < viewer (Enum-Reihenfolge)
    .order('tenant_id', { ascending: true })
    .limit(1)
  if (error) throw new Error(`memberships: ${error.message}`)
  const zeile = data?.[0] as { tenant_id: string; role: Stufe } | undefined
  return zeile ? { tenantId: zeile.tenant_id, rolle: zeile.role } : null
}

export async function loadDatabase(sb: SupabaseClient, tenantId: string): Promise<TenantBundle> {
  const [
    tenants, properties, unitCategories, units, ratePeriods, fees, guests, bookings,
    quotes, invoices, invoiceItems, payments, icalFeeds, icalEvents,
    taxRules, subscriptions,
  ] = await Promise.all([
    sb.from('tenants').select('*').eq('id', tenantId),
    sb.from('properties').select('*').order('sort_order'),
    sb.from('unit_categories').select('*').order('sort_order'),
    sb.from('units').select('*').order('sort_order'),
    sb.from('rate_periods').select('*').order('priority', { ascending: false }),
    sb.from('fees').select('*').order('sort_order'),
    sb.from('guests').select('*').order('last_name'),
    sb.from('bookings').select('*').order('arrival'),
    sb.from('quotes').select('*').order('created_at', { ascending: false }),
    sb.from('invoices').select('*').order('issue_date', { ascending: false }),
    sb.from('invoice_items').select('*').order('sort_order'),
    sb.from('payments').select('*').order('received_on', { ascending: false }),
    sb.from('ical_feeds').select('*'),
    sb.from('ical_events').select('*'),
    sb.from('tourist_tax_rules').select('*').eq('active', true).order('valid_from', { ascending: false }),
    sb.from('subscriptions').select(
      'plan_code,status,contract_term,trial_ends_at,current_period_end,units_limit,plan_pending_code,plan_pending_at'),
  ])

  const tenantRows = must(tenants, 'tenants') as Database['tenant'][]
  const tenant = tenantRows[0]
  if (!tenant) throw new Error('Tenant not found or not readable')

  const rule = (must(taxRules, 'tourist_tax_rules') as TouristTaxRule[])[0] ?? null

  return {
    db: {
      tenant,
      properties: must(properties, 'properties'),
      unit_categories: must(unitCategories, 'unit_categories'),
      units: must(units, 'units'),
      rate_periods: must(ratePeriods, 'rate_periods'),
      fees: must(fees, 'fees'),
      guests: must(guests, 'guests'),
      bookings: must(bookings, 'bookings'),
      booking_items: [],
      quotes: must(quotes, 'quotes'),
      invoices: must(invoices, 'invoices'),
      invoice_items: must(invoiceItems, 'invoice_items'),
      payments: must(payments, 'payments'),
      ical_feeds: must(icalFeeds, 'ical_feeds'),
      ical_events: must(icalEvents, 'ical_events'),
    },
    touristTax: rule,
    subscription: (must(subscriptions, 'subscriptions') as TenantBundle['subscription'][])[0] ?? null,
  }
}

/** Postgres error 23P01 is the overlap exclusion constraint — the double-booking guard. */
export function isOverlapError(message: string): boolean {
  return message.includes('bookings_no_overlap') || message.includes('23P01')
}

/**
 * Warum ein Schreibvorgang null Zeilen getroffen haben kann. Es gibt genau zwei Gründe,
 * und sie führen zu verschiedenen Handlungen — deshalb zwei Sätze und nicht einer.
 *
 * `NUR_LESEND` gilt für die Geschäftstabellen: seit 0003 hängt dort jede Schreib-Policy
 * an `tenant_writable()`, und 0023 hat die Bedingung nur erweitert, nicht entschärft.
 * Testphase abgelaufen und keine Zahlungsdaten hinterlegt heißt lesend. Das ist der bei
 * weitem häufigste Grund — häufiger als eine zu knappe Rolle, denn jeder Betrieb
 * durchläuft ihn irgendwann. Zu tun ist: bezahlen.
 *
 * `NUR_EIGENTUEMER` gilt für `tenants`. Dort hängt `tenant_update` an der Rolle und
 * NICHT an `tenant_writable` — ein `staff` wird abgewiesen, ein zahlender Eigentümer
 * nie. Zu tun ist: jemand anderen fragen. Der Hinweis aufs Abonnement wäre hier falsch
 * und schickte den Vermieter in die Rechnungsstelle statt zum Kollegen.
 */
const NUR_LESEND_GRUND =
  'Der Zugang ist derzeit nur lesend. Das ist der Fall, wenn die Testphase abgelaufen '
  + 'ist und keine Zahlungsdaten hinterlegt sind.'

/**
 * Der Grund allein reicht nicht — H5 verlangt auch den WEG ZURUECK.
 *
 * Und der ist nicht überall derselbe. Im Browser führt er zu den
 * Zahlungsdaten; in der App-Hülle gibt es ihn nicht, weil es dort den
 * Kaufweg nicht gibt (3.1.3(f), siehe `lib/kaufweg.ts`).
 *
 * **In der Hülle wird deshalb nichts dazuerfunden.** Ein „hinterlegen Sie
 * Zahlungsdaten" wäre dort genau die Aufforderung, die Apple untersagt — und
 * ein Weg, den der Vermieter in der App ohnehin nicht gehen kann. Der Grund
 * bleibt trotzdem stehen: schweigen wäre die Unehrlichkeit, gegen die dieser
 * ganze Vorgang gebaut ist.
 */
function nurLesend(): string {
  return kaufwegSichtbar()
    ? NUR_LESEND_GRUND + ' Unter Einstellungen → Abrechnung können Sie sie hinterlegen.'
    : NUR_LESEND_GRUND
}

export const NUR_EIGENTUEMER =
  'keine Berechtigung. Diese Angaben darf nur der Eigentümer oder ein Verwalter ändern.'

/**
 * Der dritte Grund, seit Vorgang 013: die Stufe reicht nicht.
 *
 * Er unterscheidet sich von `NUR_LESEND` in dem, was zu tun ist. Bei
 * `NUR_LESEND` hilft bezahlen; hier hilft nur, jemand anderen zu fragen. Wer
 * einem Mitarbeiter „hinterlegen Sie Zahlungsdaten" anzeigt, schickt ihn in die
 * Rechnungsstelle statt zu seiner Chefin.
 */
export const NUR_MIT_STUFE =
  'Dafür reicht Ihre Berechtigung nicht.'

/**
 * Die Stufe des angemeldeten Nutzers, damit `assertWrote` den richtigen Grund
 * wählen kann.
 *
 * DAS IST DIAGNOSE, KEIN TOR. Gesetzt wird sie vom AuthProvider, gelesen nur
 * hier. Wer sie fälscht, ändert einen Satz auf dem Bildschirm und sonst nichts —
 * die Prüfung steht in der Datenbank.
 */
let bekannteStufe: Stufe | null = null
export function setzeStufe(s: Stufe | null): void { bekannteStufe = s }

/**
 * Ein von RLS abgelehnter `insert` ist LAUT, nicht stumm.
 *
 * `update` und `delete` treffen null Zeilen — dafür gibt es `assertWrote`. Ein
 * `insert` dagegen wirft `42501` mit englischem Rohtext:
 *
 *     new row violates row-level security policy for table "bookings"
 *
 * Das ist der wahrscheinlichste Weg, auf dem ein `staff` oder `viewer` nach
 * Vorgang 013 zum ersten Mal an eine Grenze stößt — und ohne diese Übersetzung
 * liest eine deutschsprachige Vermieterin einen Satz aus dem Innenleben der
 * Datenbank. Nicht stumm, aber unverständlich, und das ist für sie dasselbe.
 */
export function uebersetzeSchreibfehler(message: string): string {
  if (/row-level security policy/i.test(message)) {
    return bekannteStufe === null || bekannteStufe === 'owner'
      ? nurLesend()
      : NUR_MIT_STUFE
  }
  return message
}

/**
 * Prüft, ob ein Update oder Delete tatsächlich eine Zeile getroffen hat.
 *
 * Ein von RLS abgewiesener Schreibvorgang trifft NULL Zeilen — und das ist kein
 * Fehler: PostgREST antwortet mit 204, `error` bleibt leer. Ohne ein `select` in der
 * Kette ist ein verworfener Schreibvorgang von einem erfolgreichen nicht zu
 * unterscheiden, und der Store hat den lokalen Zustand längst gesetzt. Der Benutzer
 * sieht dann den neuen Wert und keine Meldung, während die Datenbank den alten behält
 * — bis zum nächsten Neuladen.
 *
 * Das `select` ist also nicht dazu da, etwas zurückzubekommen. Es ist die einzige Art,
 * die Abweisung überhaupt zu bemerken.
 *
 * Inserts brauchen das nicht: eine abgewiesene Einfügung ist 42501 und damit laut.
 * Upserts brauchen es nicht, solange sie mit `.select().single()` enden — dann schlägt
 * spätestens `single()` auf null Zeilen an.
 *
 * Exportiert, weil nicht jeder Schreibvorgang durch diese Datei läuft: der Store und
 * ein paar Ansichten sprechen direkt mit Supabase. Sie haben dasselbe Problem und
 * sollen dieselbe Antwort geben — eine zweite Fassung dieser Prüfung wäre eine, die
 * irgendwann abweicht.
 */
export function assertWrote(
  res: { data: unknown[] | null; error: { message: string } | null },
  what: string,
  grund?: string,
): void {
  if (res.error) throw new Error(`${what}: ${uebersetzeSchreibfehler(res.error.message)}`)
  if (!res.data || res.data.length === 0) {
    // Ohne ausdrücklichen Grund entscheidet die bekannte Stufe. Ein `viewer`, dem
    // „die Testphase ist abgelaufen" angezeigt wird, sucht an der falschen Stelle
    // — und ein Inhaber, dem „Ihre Berechtigung reicht nicht" angezeigt wird,
    // erst recht.
    const gewaehlt = grund
      ?? (bekannteStufe !== null && bekannteStufe !== 'owner' ? NUR_MIT_STUFE : nurLesend())
    throw new Error(`${what}: ${gewaehlt}`)
  }
}

export async function upsertBooking(sb: SupabaseClient, b: Booking): Promise<Booking> {
  const { data, error } = await sb.from('bookings').upsert(b).select().single()
  if (error) {
    throw new Error(
      // `uebersetzeSchreibfehler` hat hier bis zum 03.08.2026 GEFEHLT, und zwar
      // ausgerechnet auf dem Weg, den ein gesperrter Betrieb zuerst geht: eine
      // Buchung anlegen. Er las `new row violates row-level security policy for
      // table "bookings"` — die englische Rohmeldung, die H5 ausdrücklich
      // ausschliesst. Der Satz war längst geschrieben; diese Zeile hat ihn nur
      // nie geholt.
      isOverlapError(error.message)
        ? 'OVERLAP'
        : `Buchung speichern: ${uebersetzeSchreibfehler(error.message)}`,
    )
  }
  return data as Booking
}

/**
 * Issue an invoice: one database call, so taking the number and freezing the
 * content cannot come apart. Idempotent — a double click returns the same row
 * rather than taking a second number.
 */
export async function issueInvoice(sb: SupabaseClient, invoiceId: string): Promise<Invoice> {
  const { data, error } = await sb.rpc('issue_invoice', { p_invoice: invoiceId })
  if (error) throw new Error(`Rechnung festschreiben: ${error.message}`)
  return data as Invoice
}

/**
 * Storno. Creates the credit note, freezes it, and marks the original cancelled —
 * one call, one transaction. Idempotent: a second click returns the credit note
 * that already exists rather than issuing a second one.
 */
export async function cancelInvoice(
  sb: SupabaseClient, invoiceId: string, reason: string,
): Promise<Invoice> {
  const { data, error } = await sb.rpc('cancel_invoice', {
    p_invoice: invoiceId, p_reason: reason || null,
  })
  if (error) throw new Error(`Storno: ${error.message}`)
  return data as Invoice
}

export async function deleteBooking(sb: SupabaseClient, id: string): Promise<void> {
  /*
   * Ohne die Prüfung verschwindet die Buchung nur vom Kalender. Sie bleibt in der
   * Datenbank stehen, das Objekt sieht frei aus — und die nächste Buchung auf denselben
   * Zeitraum läuft in `bookings_no_overlap` und meldet eine Überschneidung mit einer
   * Buchung, die der Vermieter für gelöscht hält.
   */
  assertWrote(await sb.from('bookings').delete().eq('id', id).select('id'), 'Buchung löschen')
}

export async function upsertGuest(sb: SupabaseClient, g: Guest): Promise<Guest> {
  const { data, error } = await sb.from('guests').upsert(g).select().single()
  if (error) throw new Error(`Gast speichern: ${error.message}`)
  return data as Guest
}

export async function insertQuote(sb: SupabaseClient, q: Quote): Promise<Quote> {
  // public_token has a database default; sending an empty one would violate it.
  const { public_token: _ignored, ...payload } = q
  const { data, error } = await sb.from('quotes').insert(payload).select().single()
  if (error) throw new Error(`Angebot anlegen: ${error.message}`)
  return data as Quote
}

export async function insertInvoice(
  sb: SupabaseClient, inv: Invoice, items: InvoiceItem[],
): Promise<{ invoice: Invoice; items: InvoiceItem[] }> {
  const { public_token: _ignored, ...payload } = inv
  const { data, error } = await sb.from('invoices').insert(payload).select().single()
  if (error) throw new Error(`Rechnung anlegen: ${error.message}`)
  const invoice = data as Invoice

  const rows = items.map((i) => ({ ...i, invoice_id: invoice.id, tenant_id: inv.tenant_id }))
  const { data: itemData, error: itemError } = await sb
    .from('invoice_items').insert(rows).select()
  if (itemError) {
    /*
     * Do not leave a headless invoice behind if the items fail. Gelingt auch das
     * Aufräumen nicht, steht der Entwurf trotzdem in der Liste — dann muss es in der
     * Meldung stehen, sonst sucht der Vermieter nach einer Rechnung, die er nie
     * angelegt hat. Eine Nummer ist dabei nicht verbrannt: die vergibt erst
     * `issue_invoice`.
     */
    const { data: removed } = await sb.from('invoices').delete()
      .eq('id', invoice.id).select('id')
    const rest = removed && removed.length > 0
      ? ''
      : ' Der leere Rechnungsentwurf konnte nicht entfernt werden und steht noch in der Liste.'
    throw new Error(`Rechnungspositionen: ${itemError.message}.${rest}`)
  }
  return { invoice, items: (itemData ?? []) as InvoiceItem[] }
}

export async function updateInvoice(
  sb: SupabaseClient, id: string, patch: Partial<Invoice>,
): Promise<void> {
  /*
   * Hier hängt der Zahlungsstand dran. Verworfen und unbemerkt heißt: die Rechnung
   * steht auf dem Schirm auf „bezahlt", in der Datenbank auf „offen" — und die
   * Mahnliste beim nächsten Anmelden widerspricht dem, was der Vermieter gesehen hat.
   */
  assertWrote(
    await sb.from('invoices').update(patch).eq('id', id).select('id'),
    'Rechnung aktualisieren',
  )
}

export async function updateTenant(
  sb: SupabaseClient, id: string, patch: Partial<Database['tenant']>,
): Promise<void> {
  /*
   * Die Policy `tenant_update` lässt nur `owner` und `manager` schreiben — anders als
   * die Geschäftstabellen hängt sie nicht an `tenant_writable`. Daher der andere Grund
   * in der Meldung.
   *
   * Das betrifft unter anderem die IBAN in den Stammdaten. Ein stumm verworfener
   * Kontowechsel ist die Sorte Fehler, die erst auffällt, wenn Geld ausbleibt.
   */
  assertWrote(
    await sb.from('tenants').update(patch).eq('id', id).select('id'),
    'Stammdaten speichern',
    NUR_EIGENTUEMER,
  )
}

export async function updateRatePeriod(
  sb: SupabaseClient, id: string, patch: Partial<Database['rate_periods'][number]>,
): Promise<void> {
  /*
   * Der schlimmste der stillen Fälle: der Saisonpreis steht danach nur im Browser.
   * `price()` rechnet aus dem lokalen Zustand, also bepreist das nächste Angebot mit
   * einem Wert, den die Datenbank nie bekommen hat — und nach dem Neuladen ist das
   * verschickte Angebot mit nichts mehr erklärbar.
   */
  assertWrote(
    await sb.from('rate_periods').update(patch).eq('id', id).select('id'),
    'Saison speichern',
  )
}

export async function upsertRatePeriod(
  sb: SupabaseClient, row: Database['rate_periods'][number],
): Promise<Database['rate_periods'][number]> {
  const { data, error } = await sb.from('rate_periods').upsert(row).select().single()
  if (error) throw new Error(`Saison speichern: ${error.message}`)
  return data as Database['rate_periods'][number]
}

export async function deleteRatePeriod(sb: SupabaseClient, id: string): Promise<void> {
  assertWrote(
    await sb.from('rate_periods').delete().eq('id', id).select('id'),
    'Saison löschen',
  )
}

export async function upsertFee(
  sb: SupabaseClient, row: Database['fees'][number],
): Promise<Database['fees'][number]> {
  const { data, error } = await sb.from('fees').upsert(row).select().single()
  if (error) throw new Error(`Gebühr speichern: ${error.message}`)
  return data as Database['fees'][number]
}

export async function deleteFee(sb: SupabaseClient, id: string): Promise<void> {
  assertWrote(await sb.from('fees').delete().eq('id', id).select('id'), 'Gebühr löschen')
}

/**
 * Der Trigger `units_paketgrenze` hat abgelehnt (Migration 0062).
 *
 * `code` ist die Kennung aus `details` — `unit_limit_reached`,
 * `plan_offer_required` oder `plan_band_gap`. Die Meldung ist der deutsche
 * Klartext der Datenbank und taugt als Rückfall, wenn niemand die Kennung
 * auswertet: sie nennt das Paket, seine Einheitenzahl, das nötige Paket und
 * dessen Preis. Eine Sperre ohne genannten Ausweg wird umgangen, nicht befolgt.
 */
export class PaketGrenzeError extends Error {
  constructor(message: string, readonly code: string) {
    super(message)
    this.name = 'PaketGrenzeError'
  }
}

const PAKETGRENZE = ['unit_limit_reached', 'plan_offer_required', 'plan_band_gap']

export async function upsertUnit(
  sb: SupabaseClient, row: Database['units'][number],
): Promise<Database['units'][number]> {
  const { data, error } = await sb.from('units').upsert(row).select().single()
  if (error) {
    /*
     * Die Kennung ging hier bisher verloren: `new Error('Objekt speichern: ' +
     * message)` warf `details` weg. Genau daran hängt aber der Satz, den der
     * Vermieter zu lesen bekommt — und die Möglichkeit, die beiden Grenzen
     * auseinanderzuhalten. Die Oberfläche verweigert das Anlegen zwar schon
     * vorher; dieser Weg ist der Rückfall für alles, was an ihr vorbeigeht.
     */
    const kennung = (error as { details?: string | null }).details ?? ''
    if (PAKETGRENZE.includes(kennung)) throw new PaketGrenzeError(error.message, kennung)
    throw new Error(`Objekt speichern: ${error.message}`)
  }
  return data as Database['units'][number]
}

export async function deleteUnit(sb: SupabaseClient, id: string): Promise<void> {
  // on delete restrict: a unit with bookings cannot go, and should not. Das meldet
  // sich als Fehler; die Abweisung durch RLS dagegen nur über die Zeilenzahl.
  assertWrote(await sb.from('units').delete().eq('id', id).select('id'), 'Objekt löschen')
}

/**
 * Paket wechseln. Nach oben sofort, nach unten zum Periodenende.
 *
 * Die Datenbank entscheidet, nicht der Knopf: `paket_wechseln` prüft
 * Zugehörigkeit und Rolle, lehnt eine Herabstufung unter die heutige
 * Einheitenzahl ab und rührt dabei keine Einheit an. Die benannte Kennung
 * steht wie überall in `details`; sie geht hier NICHT verloren, denn genau
 * daran hängt der Satz, den der Vermieter zu lesen bekommt.
 *
 * DIE ANTWORT WIRD ZURÜCKGEGEBEN, nicht weggeworfen. Vorher stand hier
 * `const { error } = …` — die Funktion meldet aber, WIE der Wechsel gewirkt hat
 * (`sofort`, `zum_periodenende`, `unveraendert`) und ab wann. Ohne diese Angabe
 * ist ein vorgemerkter Wechsel für den Vermieter ununterscheidbar davon, dass
 * gar nichts passiert ist — er klickt dann noch dreimal. Genau das ist am
 * 03.08.2026 passiert und war der Anlass, das hier zu ändern.
 */
export interface PaketWechselErgebnis {
  plan_code: string
  plan_pending_code: string | null
  plan_pending_at: string | null
  /** `sofort` · `zum_periodenende` · `unveraendert` */
  wirkt: 'sofort' | 'zum_periodenende' | 'unveraendert'
}

export async function paketWechseln(
  sb: SupabaseClient, tenantId: string, code: string,
): Promise<PaketWechselErgebnis> {
  const { data, error } = await sb.rpc('paket_wechseln', { p_tenant: tenantId, p_code: code })
  if (error) {
    const kennung = (error as { details?: string | null }).details
    throw new Error(kennung ? `${error.message} (${kennung})` : error.message)
  }
  return data as PaketWechselErgebnis
}

/**
 * Festhalten, dass ein Betrieb an die Grenze der Selbstbedienung gestoßen ist.
 *
 * `tenantId = null` ist der Fall aus der Ersteinrichtung: der Interessent hat
 * noch keinen Mandanten, weil `create_my_tenant()` ihn abgelehnt hat. Genau der
 * ist der wertvollste, und deshalb nimmt die Tabelle ihn auf.
 *
 * Es wird dabei NICHTS versprochen und nichts versendet — der Zustand heißt
 * `seen`, und das bedeutet „die Aussage wurde gezeigt", nicht „jemand meldet
 * sich". Die Anfrage samt Frist und Weg ins CRM ist Schnitt 4b und gesperrt.
 *
 * Der Aufrufer darf den Fehler wegwerfen, und er soll es: eine Messung darf den
 * Weg des Vermieters nie aufhalten.
 */
export async function paketAnfrageGesehen(
  sb: SupabaseClient, tenantId: string | null, unitCount: number,
): Promise<void> {
  const { error } = await sb.rpc('paket_anfrage_gesehen', {
    p_tenant: tenantId, p_units: unitCount,
  })
  if (error) throw new Error(`Grenze vermerken: ${error.message}`)
}

export async function upsertUnitCategory(
  sb: SupabaseClient, row: Database['unit_categories'][number],
): Promise<Database['unit_categories'][number]> {
  const { data, error } = await sb.from('unit_categories').upsert(row).select().single()
  if (error) throw new Error(`Kategorie speichern: ${error.message}`)
  return data as Database['unit_categories'][number]
}

export async function deleteUnitCategory(sb: SupabaseClient, id: string): Promise<void> {
  assertWrote(
    await sb.from('unit_categories').delete().eq('id', id).select('id'),
    'Kategorie löschen',
  )
}

export async function upsertTouristTax(
  sb: SupabaseClient, tenantId: string, rule: TouristTaxRule & { id?: string },
): Promise<TouristTaxRule> {
  const row = {
    id: rule.id,
    tenant_id: tenantId,
    municipality: rule.municipality,
    name: rule.name,
    amount_per_person_night: rule.amount_per_person_night,
    vat_rate: rule.vat_rate,
    exempt_under_age: rule.exempt_under_age,
    max_nights: rule.max_nights,
    valid_from: new Date().getFullYear() + '-01-01',
    active: true,
  }
  const { data, error } = await sb.from('tourist_tax_rules').upsert(row).select().single()
  if (error) throw new Error(`Ortstaxe speichern: ${error.message}`)
  return data as TouristTaxRule
}

export async function insertPayment(sb: SupabaseClient, p: Payment): Promise<Payment> {
  const { data, error } = await sb.from('payments').insert(p).select().single()
  if (error) throw new Error(`Zahlung buchen: ${error.message}`)
  return data as Payment
}

/** Atomic, per-tenant, per-year document number from the database. */
export async function nextDocumentNumber(
  sb: SupabaseClient, tenantId: string, kind: 'booking' | 'quote' | 'invoice',
): Promise<string> {
  const { data, error } = await sb.rpc('next_document_number', {
    p_tenant: tenantId, p_kind: kind,
  })
  if (error) throw new Error(`Nummernkreis: ${error.message}`)
  return data as string
}

export interface AvvPartner { firma: string; strasse: string; plz: string; ort: string; land: string }
export interface AvvVorschau {
  kennung: string; titel: string; sprache: 'de'; gilt_ab: string
  fehlend: AvvFeld[]; text: string | null; sha256: string | null; uebersetzung_en: string | null
}
export interface AvvAnnahme { kennung: string; sha256: string; partner: AvvPartner; kanal: AvvKanal }
export class AvvRpcError extends Error {
  constructor(public readonly kennung: string, public readonly hint: string | null) { super('AVV request failed') }
}
function throwAvvError(error: { details?: string | null; hint?: string | null; message: string }): never {
  if (error.details?.startsWith('avv_')) throw new AvvRpcError(error.details, error.hint ?? null)
  throw new Error(error.message)
}
export async function avvVorschau(sb: SupabaseClient, partner: AvvPartner, tenantId?: string): Promise<AvvVorschau | null> {
  const { data, error } = await avvRead(sb.rpc('avv_vorschau', {
    p_firma: partner.firma, p_strasse: partner.strasse, p_plz: partner.plz, p_ort: partner.ort, p_land: partner.land,
    ...(tenantId ? { p_tenant_id: tenantId } : {}),
  }))
  if (error) throwAvvError(error)
  if (data === null) return null
  if (!data || typeof data !== 'object' || typeof data.kennung !== 'string' || typeof data.titel !== 'string'
    || !Array.isArray(data.fehlend) || (data.text !== null && typeof data.text !== 'string')
    || (data.sha256 !== null && (typeof data.sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(data.sha256)))
    || (data.uebersetzung_en !== null && typeof data.uebersetzung_en !== 'string')
    || (data.fehlend.length === 0 && (typeof data.text !== 'string' || !data.text.trim() || !data.sha256))) {
    throw new AvvRpcError('avv_fassung_unvollstaendig', null)
  }
  return data as AvvVorschau
}

/** A new optional legal read must never hold an existing login indefinitely. */
async function avvRead<T>(request: PromiseLike<T> & { abortSignal?: (signal: AbortSignal) => PromiseLike<T> }): Promise<T> {
  const controller = new AbortController()
  const query = request.abortSignal?.(controller.signal) ?? request
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      Promise.resolve(query),
      new Promise<T>((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error('avv_timeout')) }, 5000) }),
    ])
  } finally { clearTimeout(timer) }
}
export async function avvStatus(sb: SupabaseClient, tenantId: string): Promise<AvvStatus | null> {
  const { data, error } = await avvRead(sb.rpc('avv_status', { p_tenant_id: tenantId }))
  if (error) throwAvvError(error)
  if (!data || typeof data.angenommen !== 'boolean' || typeof data.pflicht_aktiv !== 'boolean' || typeof data.darf_annehmen !== 'boolean') return null
  if (data.fassung !== null && (!data.fassung || typeof data.fassung.kennung !== 'string' || typeof data.fassung.titel !== 'string' || typeof data.fassung.annahme_pflicht_ab !== 'string')) return null
  return data as AvvStatus
}
export async function avvPartnerLaden(sb: SupabaseClient, tenantId: string): Promise<AvvPartner> {
  const { data, error } = await avvRead(sb.from('tenants').select('legal_name,street,postcode,city').eq('id', tenantId).single())
  if (error) throw new Error(error.message)
  return { firma: data.legal_name ?? '', strasse: data.street ?? '', plz: data.postcode ?? '', ort: data.city ?? '', land: '' }
}
export async function avvAnnehmen(sb: SupabaseClient, tenantId: string, a: AvvAnnahme): Promise<void> {
  const { error } = await sb.rpc('avv_annehmen', {
    p_tenant_id: tenantId, p_kennung: a.kennung, p_sha256: a.sha256,
    p_firma: a.partner.firma, p_strasse: a.partner.strasse, p_plz: a.partner.plz,
    p_ort: a.partner.ort, p_land: a.partner.land, p_kanal: a.kanal,
  })
  if (error) throwAvvError(error)
}
export interface AvvNachweis {
  id: string; tenant_id: string; fassung_kennung: string; angenommen_at: string
  angenommen_von_email: string; ausfertigung_sha256: string
}
export async function avvAnnahmenLaden(sb: SupabaseClient, tenantId: string): Promise<AvvNachweis[]> {
  const { data, error } = await avvRead(sb.from('avv_annahmen')
    .select('id,tenant_id,fassung_kennung,angenommen_at,angenommen_von_email,ausfertigung_sha256')
    .eq('tenant_id', tenantId).order('angenommen_at', { ascending: true }))
  if (error) throw new Error(error.message)
  return ((data ?? []) as AvvNachweis[]).filter(row => row.tenant_id === tenantId)
}
export async function avvAusfertigungLaden(sb: SupabaseClient, tenantId: string, annahmeId: string): Promise<string> {
  const { data, error } = await avvRead(sb.from('avv_annahmen').select('ausfertigung')
    .eq('tenant_id', tenantId).eq('id', annahmeId).single())
  if (error) throw new Error(error.message)
  if (typeof data?.ausfertigung !== 'string') throw new Error('Agreement unavailable')
  return data.ausfertigung
}

export async function createMyTenant(
  sb: SupabaseClient,
  args: { name: string; country: string; locale: string; unitCount: number; unitKind: 'room' | 'apartment'; avv?: AvvAnnahme },
): Promise<string> {
  const { data, error } = await sb.rpc('create_my_tenant', {
    p_name: args.name,
    p_country: args.country,
    p_locale: args.locale,
    p_unit_count: args.unitCount,
    p_unit_kind: args.unitKind,
    ...(args.avv ? {
      p_avv_kennung: args.avv.kennung, p_avv_sha256: args.avv.sha256,
      p_vp_firma: args.avv.partner.firma, p_vp_strasse: args.avv.partner.strasse,
      p_vp_plz: args.avv.partner.plz, p_vp_ort: args.avv.partner.ort, p_vp_land: args.avv.partner.land,
      p_kanal: args.avv.kanal,
    } : {}),
  })
  if (error) throwAvvError(error)
  return data as string
}

export interface HousekeepingRow {
  unit_id: string
  unit_name: string
  property_name: string
  booking_id: string | null
  guest_name: string | null
  departed_on: string
  next_arrival: string | null
  turnover: boolean
  done: boolean
  done_at: string | null
}

export async function loadHousekeeping(
  sb: SupabaseClient, tenantId: string, day: string,
): Promise<HousekeepingRow[]> {
  const { data, error } = await sb.rpc('housekeeping_for_day', {
    p_tenant: tenantId, p_day: day,
  })
  if (error) throw new Error(`Reinigung laden: ${error.message}`)
  return (data ?? []) as HousekeepingRow[]
}

export async function setHousekeepingDone(
  sb: SupabaseClient, tenantId: string, unitId: string, day: string, done: boolean,
): Promise<void> {
  /*
   * Der Haken ist ein Insert, das Wegnehmen ein Delete — und nur das Delete ist still.
   * Ein abgewiesenes Insert ist 42501 und meldet sich von selbst; ein Delete auf null
   * Zeilen ist zufrieden. Ohne die Prüfung bliebe die Reinigung nach dem Neuladen
   * erledigt, obwohl sie es nicht ist, und die Zimmerfrau geht daran vorbei.
   *
   * Der Aufrufer schickt `done = false` nur für eine Zeile, die er als erledigt
   * geladen hat — null Zeilen heißt hier also verworfen, nicht „war schon weg".
   */
  const res = done
    ? await sb.from('housekeeping_done')
        .upsert({ tenant_id: tenantId, unit_id: unitId, due_date: day })
        .select('unit_id')
    : await sb.from('housekeeping_done').delete()
        .eq('tenant_id', tenantId).eq('unit_id', unitId).eq('due_date', day)
        .select('unit_id')
  assertWrote(res, 'Reinigung speichern')
}
